import styles from './shipmentPreparationList.module.css'
import { Data, Data2, DataTypeList, describeHelpers, Labels } from "../../../../features/data-types"
import { Action, Column, FieldType, FieldValue, Badge, ModalId, useUIActions, Link } from "../../../../features/ui"
import { TableName } from "../../../../tables"
import { useMemo, useState } from "react"
import { DeliveryRoadmap, Roadmap } from "../../../logistics"
import { PreparationStatus } from "../../../common"
import { firstItem } from "../../../../utils/listUtils"
import { DataValue2 } from "../../../../features/data-types/types"
import { useLocation } from "react-router-dom"
import { useFind } from "../../../../state/reducers/hooks"
import { RoutePath, useRoutesHooks } from "../../../../features/routes"
import { Delivery, DeliveryLink, DeliveryOrder, DownloadDeliveryNotesModal, ShipmentLinksTable } from '../../../deliveries'
import { DownloadTruckPreparationPDFModal } from '../download-truck-preparation-pdf-modal/downloadTruckPreparationPDFModal'

type Props = {
    roadmaps: Roadmap[]
    deliveryRoadmaps: DeliveryRoadmap[]
    shipments: Delivery[]
    shipmentOrders: DeliveryOrder[]
}

export const ShipmentPreparationList = (props: Props) => {
    const { roadmaps, deliveryRoadmaps, shipments, shipmentOrders } = props

    const [currentShipments, setCurrentShipments] = useState<Delivery[]>([])
    const [currentShipmentOrders, setCurrentShipmentOrders] = useState<DeliveryOrder[]>([])
    const [currentRoadmap, setCurrentRoadmap] = useState<Roadmap>()

    const { describeCode } = describeHelpers

    const { openModal } = useUIActions()

    const { pathname } = useLocation()
    const { getRoutePath } = useRoutesHooks()
    const find = useFind()

    const findRoadmap = (id?: string) => roadmaps.find(roadmap => roadmap.id === id)
    const findRoadmapShipments = (roadmapId?: string) => {
        const shipmentIds = deliveryRoadmaps
            .filter(deliveryRoadmap => deliveryRoadmap.roadmapId === roadmapId)
            .map(deliveryRoadmap => deliveryRoadmap.deliveryId)
        return shipments.filter(shipment => shipmentIds.includes(shipment.id))
    }
    const findRoadmapShipmentOrders = (shipments: Delivery[]) => {
        const shipmentOrderIds = shipments.map(shipment => shipment.orderId)
        return shipmentOrders.filter(shipmentOrder => shipmentOrderIds.includes(shipmentOrder.id))
    }

    const rows: Data2[] = useMemo(() => roadmaps
        .sort((roadmap_A, roadmap_B) => roadmap_A.date.localeCompare(roadmap_B.date))
        .map(roadmap => {
            const roadmapShipments = findRoadmapShipments(roadmap.id)
            return {
                ...roadmap,
                code: describeCode(roadmap),
                shipments: roadmapShipments
            }
        })
    , [roadmaps, deliveryRoadmaps])

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.TEXT,
            label: 'Hoja de Ruta'
        },
        {
            name: 'vehicleId',
            type: FieldType.TABLE,
            table: TableName.VEHICLES,
            label: 'Vehículo',
            sortValue: (value?: FieldValue) => (find(TableName.VEHICLES, value as string) as { name?: string })?.name ?? ''
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'shipments',
            type: FieldType.TEXT,
            label: 'Envíos',
            sortable: false,
            render: (value?: DataValue2) => {
                const roadmapShipments = value as Delivery[]
                const multipleShipments = roadmapShipments.length > 1
                const shipment = firstItem(roadmapShipments)!
                return multipleShipments ?
                    <Link
                        text={`${roadmapShipments.length} Envíos`}
                        kind="primary"
                    /> :
                    <DeliveryLink delivery={shipment} />
            }
        },
        {
            name: 'preparationStatus',
            type: FieldType.TEXT,
            label: 'Estado de Preparación',
            sortValue: (value?: FieldValue) => Labels.preparationStatusLabel(value as PreparationStatus),
            render: (value?: FieldValue) => {
                const status = value as PreparationStatus
                return <Badge label={Labels.preparationStatusLabel(status)} />
            }
        }
    ]

    const onDownloadDeliveryNotes = (id?: string) => {
        const roadmapShipments = findRoadmapShipments(id)
        setCurrentShipments(roadmapShipments)
        setCurrentShipmentOrders(findRoadmapShipmentOrders(roadmapShipments))
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onDownloadTruckPreparation = (id?: string) => {
        setCurrentRoadmap(findRoadmap(id))
        openModal(ModalId.DOWNLOAD_TRUCK_PREPARATION_PDF)
    }

    const actions = (id: string): Action[] => {
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Hoja de Ruta', href: `${pathname}/${id}` }
        ]

        const roadmap = findRoadmap(id)
        const hasDeliveries = findRoadmapShipments(roadmap?.id).length > 0

        if (hasDeliveries) {
            actionList.push({ icon: 'download', tooltipLines: ['Descargar', 'Remitos'], kind: 'tertiary', onClick: onDownloadDeliveryNotes })
            actionList.push({ icon: 'printer', tooltipLines: ['Imprimir', 'Preparación'], kind: 'tertiary', onClick: onDownloadTruckPreparation })
        }
        if (roadmap?.preparationStatus === PreparationStatus.PENDING) {
            actionList.push({ icon: 'truck', tooltipLines: ['Preparar', 'Camión'], href: `${getRoutePath(RoutePath.ROADMAPS, id)}/${RoutePath.PREPARE_TRUCK}` })
        }

        return actionList
    }

    const renderDetail = (row: Data2) => {
        const shipments = row.shipments as Delivery[]
        return (
            <div className={styles.detailContainer}>
                <div className={styles.tableContainer}>
                    <ShipmentLinksTable shipments={shipments} />
                </div>
            </div> 
        )
    }

    return (
        <>
            <DataTypeList
                columns={columns}
                rows={rows as Data[]}
                readOnly
                actions={actions}
                renderDetail={renderDetail}
            />
            <DownloadDeliveryNotesModal
                deliveries={currentShipments}
                deliveryOrders={currentShipmentOrders}
            />
            <DownloadTruckPreparationPDFModal roadmap={currentRoadmap} />
        </>
    )
}
